import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useT } from '../i18n/translations';
import { PRODUCTS } from '../data/catalog';
import Drawer from './Drawer';
import Icon from './Icon';
import './AccountDrawer.css';

export default function AccountDrawer() {
  const { lang, user, logout, fx, vipTier, nextTier, orders, wishlist } = useApp();
  const t = useT(lang);
  const [tab, setTab] = useState('overview');

  if (!user) return null;

  const lifetime = user.lifetimeSpent || 0;
  const progress = nextTier
    ? Math.min(100, ((lifetime - vipTier.threshold) / (nextTier.threshold - vipTier.threshold)) * 100)
    : 100;

  return (
    <Drawer id="account" title={t.account.welcome} icon="user" width={460}>
      <div className="nx-acc__hero">
        <div className="nx-acc__avatar">{user.avatar}</div>
        <div className="nx-acc__name">{user.name}</div>
        <div className="nx-acc__email">{user.email}</div>
        <div className="nx-acc__vip">
          <span
            className="nx-acc__vip-badge"
            style={{
              background:
                typeof vipTier.color === 'string' && vipTier.color.startsWith('#')
                  ? vipTier.color
                  : vipTier.color,
            }}
          />
          <strong>{vipTier.name}</strong>
          <span>· {vipTier.rebate} 終身回饋</span>
        </div>
        {nextTier && (
          <div className="nx-acc__progress">
            <div className="nx-acc__progress-bar">
              <span style={{ width: progress + '%' }} />
            </div>
            <div className="nx-acc__progress-label">
              {t.account.nextTier} <strong>{nextTier.name}</strong>：再消費{' '}
              {fx(nextTier.threshold - lifetime)}
            </div>
          </div>
        )}
        <div className="nx-acc__stats">
          <div>
            <strong>{user.nexusPoints?.toLocaleString() || 0}</strong>
            <span>{t.account.points}</span>
          </div>
          <div>
            <strong>{orders.length}</strong>
            <span>{t.account.orders}</span>
          </div>
          <div>
            <strong>{wishlist.length}</strong>
            <span>{t.account.wishlist}</span>
          </div>
        </div>
      </div>

      <div className="nx-acc__tabs">
        {[
          { id: 'overview', label: '總覽', icon: 'sparkle' },
          { id: 'orders', label: t.account.orders, icon: 'box' },
          { id: 'wishlist', label: t.account.wishlist, icon: 'heart' },
          { id: 'settings', label: t.account.settings, icon: 'support' },
        ].map((tt) => (
          <button
            key={tt.id}
            className={`nx-acc__tab ${tab === tt.id ? 'is-active' : ''}`}
            onClick={() => setTab(tt.id)}
          >
            <Icon name={tt.icon} size={14} />
            {tt.label}
          </button>
        ))}
      </div>

      <div className="nx-acc__pane">
        {tab === 'overview' && (
          <div className="nx-acc__overview">
            <div className="nx-acc__card">
              <Icon name="mail" size={18} />
              <div>
                <strong>{user.email}</strong>
                <span>會員加入：{user.joinedAt}</span>
              </div>
            </div>
            <div className="nx-acc__card">
              <Icon name="crown" size={18} />
              <div>
                <strong>會員等級 {vipTier.name}</strong>
                <span>終身 {vipTier.rebate} 回饋於每筆訂單自動套用</span>
              </div>
            </div>
            <div className="nx-acc__card">
              <Icon name="discount" size={18} />
              <div>
                <strong>優惠碼</strong>
                <span>新會員可於購物車輸入「QQCARD5」立享 5% 折扣</span>
              </div>
            </div>
          </div>
        )}
        {tab === 'orders' && (
          <div className="nx-acc__orders">
            {orders.length === 0 ? (
              <div className="nx-acc__empty">
                尚無訂單，前往商城開始你的第一次儲值！
              </div>
            ) : (
              orders.slice(0, 8).map((o) => (
                <div key={o.id} className="nx-acc__order">
                  <div className="nx-acc__order-head">
                    <span className="nx-acc__order-id">#{o.id}</span>
                    <span className="badge badge-soft">已完成</span>
                  </div>
                  <div className="nx-acc__order-body">
                    {o.items.map((it) => {
                      const prod = PRODUCTS.find((p) => p.id === it.productId);
                      return (
                        <div key={it.key} className="nx-acc__order-item">
                          <span
                            className="nx-acc__order-chip brand-tile"
                            style={{ '--brand-bg': it.color || prod?.bg || '#14161c' }}
                          >
                            {prod?.logoUrl ? (
                              <img src={prod.logoUrl} alt={prod.en || it.name} loading="lazy" />
                            ) : (
                              <em>{prod?.logoText || it.name.slice(0, 2)}</em>
                            )}
                          </span>
                          <span className="nx-acc__order-name">{it.name} × {it.qty}</span>
                          <span>{fx(it.price * it.qty)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="nx-acc__order-foot">
                    <span>共 {o.items.length} 項</span>
                    <strong>{fx(o.total)}</strong>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        {tab === 'wishlist' && (
          <div className="nx-acc__empty">
            {wishlist.length === 0
              ? '尚無收藏。在商品卡上點愛心圖示即可加入。'
              : `共 ${wishlist.length} 件收藏中的商品`}
          </div>
        )}
        {tab === 'settings' && (
          <div className="nx-acc__settings">
            <label>
              <span>Email 通知</span>
              <input type="checkbox" defaultChecked />
            </label>
            <label>
              <span>簡訊通知</span>
              <input type="checkbox" defaultChecked />
            </label>
            <label>
              <span>電子報訂閱</span>
              <input type="checkbox" defaultChecked />
            </label>
            <label>
              <span>二階段驗證 (2FA)</span>
              <input type="checkbox" />
            </label>
          </div>
        )}
      </div>

      <button className="nx-acc__logout" onClick={logout}>
        <Icon name="logout" size={14} /> {t.account.logout}
      </button>
    </Drawer>
  );
}
